import { inject, Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { LoadingBarService } from '@ngx-loading-bar/core';
import { LoaderMessageService } from '../../../../common/auth/loader-message.service';
import * as mapboxgl from 'mapbox-gl';
import MapboxDraw from '@mapbox/mapbox-gl-draw';
import * as turf from '@turf/turf';
import { ErrorListControl, type Issue, type TracesActivity, type WhispActivity } from './map-error-list.control';
import { enforceLeftHandRings, isClockwise } from './cluster-dissolve';
import { holeDefect } from '../map-geometry.utils';

import type { Feature, FeatureCollection, GeoJsonProperties, Geometry, Polygon as GJPolygon } from 'geojson';

const SRC_KINKS = 'val-kinks-src';
const LYR_KINKS = 'val-kinks-layer';
const SRC_SELECTED = 'val-selected-issue-src';
const LYR_SELECTED_FILL = 'val-selected-issue-fill';
const LYR_SELECTED_LINE = 'val-selected-issue-line';
const INTERSECTION_AREA_EPS = 1e-8;
const KINK_ZOOM = 16;
const PROGRESS_KEYS: Record<string, { phase: string; counted: string }> = {
  checking: { phase: 'Map_stage_checking_tr', counted: 'Map_checking_progress_tr' },
  overlaps: { phase: 'Map_stage_overlaps_tr', counted: 'Map_overlaps_progress_tr' },
  preparing: { phase: 'Map_stage_preparing_tr', counted: 'Map_preparing_progress_tr' },
  repairing: { phase: 'Map_stage_repairing_tr', counted: 'Map_repairing_progress_tr' },
};

const emptyFC = (): FeatureCollection => ({ type: 'FeatureCollection', features: [] });

function orientedFeatureKeepId(f: Feature<GJPolygon>): Feature<GJPolygon> {
  const fixed = enforceLeftHandRings((f.geometry?.coordinates ?? []) as [number, number][][]);
  const out: Feature<GJPolygon> = {
    type: 'Feature',
    properties: { ...(f.properties ?? {}) },
    geometry: { type: 'Polygon', coordinates: fixed },
  };
  (out as any).id = (f as any).id;
  return out;
}
function bboxLike(bb: [number, number, number, number]): mapboxgl.LngLatBoundsLike {
  return [
    [bb[0], bb[1]],
    [bb[2], bb[3]],
  ];
}
type ClusterReply =
  | { type: 'clusterRepaired'; payload: { features: Feature<GJPolygon>[]; deleteIds: string[] } }
  | { type: 'clusterSkipped'; reason: 'kinks' | 'none' };

@Injectable({ providedIn: 'root' })
export class DrawValidationService {
  private readonly translate = inject(TranslateService);
  private readonly loader = inject(LoadingBarService).useRef('map-fix-all');
  private readonly loaderMessage = inject(LoaderMessageService);
  private repairWorker: Worker | null = null;
  private bulkFixing = false;
  private singleFixing = false;
  private operation = 0;
  private rejectRepair?: (error: Error) => void;
  private onStateChange?: () => void;
  get isBusy() {
    return this.bulkFixing || this.singleFixing || this.pending || this.timer !== null;
  }
  get isRepairing() {
    return this.bulkFixing || this.singleFixing;
  }

  cancel() {
    this.operation++;
    if (this.timer) clearTimeout(this.timer);
    this.timer = null;
    this.worker?.terminate();
    this.worker = null;
    this.repairWorker?.terminate();
    this.repairWorker = null;
    this.rejectRepair?.(new Error('Cancelled'));
    this.rejectRepair = undefined;
    this.pending = false;
    this.needsRerun = false;
    this.bulkFixing = false;
    this.singleFixing = false;
    this.validationFinished?.();
    this.validationFinished = undefined;
    this.loader.complete();
    this.loaderMessage.set(null);
    this.loaderMessage.cancelMap.set(null);
    this.loaderMessage.mapProgress.set(null);
    this.ctrl?.setErrors([]);
    this.issues = [];
    try {
      (this.map?.getSource(SRC_KINKS) as mapboxgl.GeoJSONSource)?.setData(emptyFC());
      (this.map?.getSource(SRC_SELECTED) as mapboxgl.GeoJSONSource)?.setData(emptyFC());
    } catch {}
    this.ctrl?.setBusy(false);
    this.ctrl?.setNotice(this.translate.instant('Map_processing_cancelled_tr'));
    if (this.map) this.initWorker();
    this.onStateChange?.();
  }

  private showProgress(payload: { stage: string; completed: number; total: number }) {
    const keys = PROGRESS_KEYS[payload.stage];
    if (!keys) return;
    const fraction = payload.total > 0 ? payload.completed / payload.total : 0;
    this.ctrl?.setProgress(this.translate.instant(keys.phase), fraction);
    if (this.loaderMessage.message() === 'fixingMap') {
      this.loaderMessage.mapProgress.set(this.translate.instant(keys.counted, payload));
    }
  }
  private validationFinished?: () => void;
  private issues: Issue[] = [];
  get issueFeatureIds(): Set<string> {
    return new Set(this.issues.flatMap((issue) => (issue.kind === 'overlap' ? issue.ids : (issue.featureIds ?? []))));
  }
  private map: mapboxgl.Map | null = null;
  private draw: MapboxDraw | null = null;
  private ctrl: ErrorListControl | null = null;
  private onAfterFix?: () => void;

  private worker: Worker | null = null;
  private pending = false;
  private needsRerun = false;
  private lastRun = 0;
  private throttleMs = 220;
  private timer: ReturnType<typeof setTimeout> | null = null;
  private schedule = () => this.recompute();

  attach(map: mapboxgl.Map, draw: MapboxDraw, options?: { onAfterFix?: () => void; onStateChange?: () => void }) {
    this.map = map;
    this.draw = draw;
    this.onAfterFix = options?.onAfterFix;
    this.onStateChange = options?.onStateChange;

    this.ensureKinkLayer();

    this.ctrl = new ErrorListControl(
      (it) => this.zoomToIssue(it),
      (ov) => this.fixSingleOverlap(ov),
      () => void this.fixAll(),
      () => this.cancel(),
      (key) => this.translate.instant(key),
      () => this.recompute()
    );
    try {
      this.map.addControl(this.ctrl as any, 'bottom-right');
    } catch {}

    this.initWorker();

    const schedule = this.schedule;
    map.on('draw.create', schedule);
    map.on('draw.delete', schedule);
    map.on('draw.combine', schedule);
    map.on('draw.uncombine', schedule);
    map.on('draw.update', schedule);

    Promise.resolve().then(() => this.computeAndRender());
  }

  detach() {
    if (!this.map) return;
    if (this.timer) clearTimeout(this.timer);
    this.timer = null;
    this.operation++;
    this.rejectRepair?.(new Error('Map closed'));
    this.rejectRepair = undefined;
    this.bulkFixing = false;
    this.singleFixing = false;
    for (const event of ['draw.create', 'draw.delete', 'draw.combine', 'draw.uncombine', 'draw.update']) {
      this.map.off(event, this.schedule);
    }
    this.pending = false;
    this.needsRerun = false;
    this.validationFinished?.();
    this.validationFinished = undefined;
    this.loader.complete();
    this.repairWorker?.terminate();
    this.repairWorker = null;
    this.loaderMessage.set(null);
    this.loaderMessage.cancelMap.set(null);
    this.loaderMessage.mapProgress.set(null);
    try {
      if (this.ctrl) (this.map as any).removeControl?.(this.ctrl);
    } catch {}
    try {
      if (this.map.getLayer(LYR_KINKS)) this.map.removeLayer(LYR_KINKS);
      if (this.map.getSource(SRC_KINKS)) this.map.removeSource(SRC_KINKS);
      if (this.map.getLayer(LYR_SELECTED_LINE)) this.map.removeLayer(LYR_SELECTED_LINE);
      if (this.map.getLayer(LYR_SELECTED_FILL)) this.map.removeLayer(LYR_SELECTED_FILL);
      if (this.map.getSource(SRC_SELECTED)) this.map.removeSource(SRC_SELECTED);
    } catch {}
    if (this.worker) {
      this.worker.terminate();
      this.worker = null;
    }
    this.ctrl = null;
    this.issues = [];
    this.map = null;
    this.draw = null;
    this.onAfterFix = undefined;
    this.onStateChange = undefined;
  }

  showTracesActivity(state: TracesActivity) {
    this.ctrl?.setTracesActivity(state, this.translate.instant(`traces_status_${state}_tr`));
  }

  showWhispActivity(state: WhispActivity) {
    this.ctrl?.setWhispActivity(state, state === 'idle' ? '' : this.translate.instant(`whisp_chip_${state}_tr`));
  }

  public recompute() {
    if (this.bulkFixing) return;
    this.ctrl?.setNotice('');
    this.ctrl?.setBusy(true);
    const now = Date.now();
    if (this.pending) {
      this.needsRerun = true;
      return;
    }
    if (this.timer) clearTimeout(this.timer);
    if (now - this.lastRun < this.throttleMs) {
      this.timer = setTimeout(
        () => {
          this.timer = null;
          this.recompute();
        },
        this.throttleMs - (now - this.lastRun)
      );
      return;
    }
    this.lastRun = now;
    this.computeAndRender();
  }

  private initWorker() {
    try {
      const worker = (this.worker = new Worker(new URL('./draw-validation.worker', import.meta.url), {
        type: 'module',
      }));
      worker.onerror = () => {
        if (this.worker !== worker) return;
        this.pending = false;
        this.needsRerun = false;
        this.ctrl?.setBusy(false);
        this.worker?.terminate();
        this.worker = null;
        this.onStateChange?.();
        this.validationFinished?.();
        this.validationFinished = undefined;
      };
      worker.onmessage = (ev: MessageEvent) => {
        if (this.worker !== worker) return;
        const { type, payload } = ev.data || {};
        if (type === 'progress') {
          this.showProgress(payload);
          return;
        }
        if (type !== 'result') return;
        this.pending = false;

        if (this.needsRerun) {
          this.needsRerun = false;
          this.recompute();
          return;
        }

        try {
          const src = this.map?.getSource(SRC_KINKS) as mapboxgl.GeoJSONSource | undefined;
          src?.setData(payload.kinkPoints);
        } catch {}

        const issues: Issue[] = (payload.issues as any[]).map((e) => {
          if (e.kind === 'kinks') return { ...e, center: e.center as mapboxgl.LngLatLike };
          if (e.kind === 'orientation' || e.kind === 'hole') return { ...e, bbox: bboxLike(e.bbox) };
          if (e.kind === 'overlap') return { ...e, bbox: bboxLike(e.bbox) };
          return e;
        });

        this.ctrl?.setErrors(issues);
        this.issues = issues;
        this.ctrl?.setBusy(false);
        this.onStateChange?.();
        this.validationFinished?.();
        this.validationFinished = undefined;

        if (this.needsRerun) {
          this.needsRerun = false;
          this.recompute();
        }
      };
    } catch {
      this.worker = null;
    }
  }

  private computeAndRender() {
    if (!this.map || !this.draw) return;
    this.ctrl?.setBusy(true);
    try {
      (this.map.getSource(SRC_SELECTED) as mapboxgl.GeoJSONSource)?.setData(emptyFC());
    } catch {}

    const polys = this.getAllPolygonsWithIds();
    if (this.worker) {
      this.pending = true;
      this.onStateChange?.();
      this.worker.postMessage({ type: 'compute', payload: { polys } });
      return;
    }

    const issues: Issue[] = [];
    for (let i = 0; i < polys.length; i++) {
      if (holeDefect(polys[i].feat)) {
        issues.push({
          id: `hole-${i}`,
          featureIds: [polys[i].id],
          kind: 'hole',
          message: `Polygon ${i + 1} has a hole outside or across its outline`,
          bbox: bboxLike(turf.bbox(polys[i].feat as any) as [number, number, number, number]),
        });
      }
      const coords = (polys[i].feat.geometry.coordinates ?? []) as [number, number][][];
      if (coords.length) {
        if (!isClockwise(coords[0])) {
          const bb = turf.bbox(polys[i].feat as any) as [number, number, number, number];
          issues.push({
            id: `orient-${i}-outer`,
            featureIds: [polys[i].id],
            kind: 'orientation',
            message: `Polygon ${i + 1} outer ring is not clockwise (left-hand rule).`,
            bbox: bboxLike(bb),
          });
        }
        for (let r = 1; r < coords.length; r++) {
          if (isClockwise(coords[r])) {
            const bb = turf.bbox(polys[i].feat as any) as [number, number, number, number];
            issues.push({
              id: `orient-${i}-hole-${r}`,
              featureIds: [polys[i].id],
              kind: 'orientation',
              message: `Polygon ${i + 1} hole #${r} is not counter-clockwise (left-hand rule).`,
              bbox: bboxLike(bb),
            });
          }
        }
      }
    }
    try {
      const src = this.map.getSource(SRC_KINKS) as mapboxgl.GeoJSONSource | undefined;
      src?.setData(emptyFC());
    } catch {}
    this.ctrl?.setErrors(issues);
    this.issues = issues;
    this.ctrl?.setBusy(false);
    this.onStateChange?.();
    this.validationFinished?.();
    this.validationFinished = undefined;
  }

  private async fixAll() {
    if (!this.draw || this.bulkFixing || this.pending) return;
    this.bulkFixing = true;
    const operation = ++this.operation;
    this.loader.start();
    this.loaderMessage.set('fixingMap');
    this.loaderMessage.cancelMap.set(() => this.cancel());
    this.onStateChange?.();
    this.ctrl?.setBusy(true, this.translate.instant('Map_stage_repairing_tr'));
    this.ctrl?.setNotice('');
    try {
      await new Promise<void>((resolve) => setTimeout(resolve, 40));
      if (operation !== this.operation) return;
      const draw = this.draw;
      if (!draw) return;
      const before = draw.getAll();
      const result = await new Promise<{ features: Feature[]; failedGroups: number }>((resolve, reject) => {
        this.rejectRepair = reject;
        const worker = new Worker(new URL('./draw-validation.worker', import.meta.url), { type: 'module' });
        this.repairWorker = worker;
        worker.onmessage = ({ data }) => {
          if (this.repairWorker !== worker || operation !== this.operation) return;
          if (data.type === 'progress') {
            this.showProgress(data.payload);
            return;
          }
          if (data.type === 'repaired') resolve(data.payload);
          else reject(new Error(data.message ?? 'Map repair failed'));
        };
        worker.onerror = () => reject(new Error('Map repair worker failed'));
        worker.postMessage({ type: 'repair', payload: { polys: this.getAllPolygonsWithIds() } });
      });
      if (this.draw !== draw || operation !== this.operation) return;
      if (JSON.stringify(draw.getAll()) !== JSON.stringify(before)) throw new Error('Map changed during repair');
      const others = before.features.filter((feature) => feature.geometry.type !== 'Polygon');
      try {
        draw.set({ type: 'FeatureCollection', features: [...others, ...result.features] });
      } catch (error) {
        draw.set(before);
        throw error;
      }
      this.onAfterFix?.();
      this.bulkFixing = false;
      await new Promise<void>((resolve) => {
        this.validationFinished = resolve;
        this.recompute();
      });
      const notice = this.afterRepairNotice(result.failedGroups);
      if (notice) this.ctrl?.setNotice(notice);
    } catch (error) {
      if (operation !== this.operation) return;
      console.error('Could not repair all map issues', error);
      this.ctrl?.setNotice('Could not finish repairing map issues. Please review the polygons and try again.');
    } finally {
      if (operation === this.operation) {
        this.bulkFixing = false;
        this.loader.complete();
        this.loaderMessage.set(null);
        this.loaderMessage.cancelMap.set(null);
        this.loaderMessage.mapProgress.set(null);
        this.repairWorker?.terminate();
        this.repairWorker = null;
        this.ctrl?.setBusy(false);
        this.rejectRepair = undefined;
        this.onStateChange?.();
      }
    }
  }

  private afterRepairNotice(failedGroups: number): string {
    const notices: string[] = [];
    if (failedGroups) {
      notices.push(
        `${failedGroups} overlap groups could not be repaired automatically. Please review the remaining issues.`
      );
    }
    const selfIntersections = this.issues.filter((issue) => issue.kind === 'kinks').length;
    if (selfIntersections) {
      notices.push(this.translate.instant('Map_self_intersections_left_tr', { count: selfIntersections }));
    }
    return notices.join(' ');
  }

  private zoomToIssue(it: Issue) {
    if (!this.map) return;
    try {
      const ids = it.kind === 'overlap' ? it.ids : (it.featureIds ?? []);
      const features = ids.map((id) => this.draw?.get(id)).filter((feature): feature is Feature => !!feature);
      (this.map.getSource(SRC_SELECTED) as mapboxgl.GeoJSONSource)?.setData({ type: 'FeatureCollection', features });
      if (it.kind === 'kinks') this.map.flyTo({ center: it.center, zoom: KINK_ZOOM });
      else this.map.fitBounds((it as any).bbox, { padding: 40 });
    } catch {}
  }

  private async fixSingleOverlap(issue: Extract<Issue, { kind: 'overlap' }>) {
    if (!this.draw || this.isBusy) return;
    this.singleFixing = true;
    const operation = ++this.operation;
    try {
      const before = this.draw.getAll();
      this.ctrl?.setBusy(true, this.translate.instant('Map_stage_repairing_tr'));
      await new Promise<void>((resolve) => setTimeout(resolve, 30));
      if (!this.draw || operation !== this.operation) return;

      const reply = await this.discoverClusterInWorker(operation, issue.ids).catch(() => null);
      if (!this.draw || operation !== this.operation) return;
      if (!reply) {
        this.recompute();
        return;
      }
      if (reply.type === 'clusterSkipped') {
        if (reply.reason === 'kinks') {
          this.ctrl?.setNotice(this.translate.instant('Map_merge_needs_manual_kinks_tr'));
        }
        this.recompute();
        return;
      }
      const out = reply.payload.features;
      const delIds = reply.payload.deleteIds;
      if (!out.length) {
        this.recompute();
        return;
      }
      try {
        if (JSON.stringify(this.draw.getAll()) !== JSON.stringify(before)) {
          this.recompute();
          return;
        }
        if (out.length === 1) (this.draw as any).add(out[0] as any);
        else if (out.length > 1) (this.draw as any).add({ type: 'FeatureCollection', features: out } as any);
        this.draw.delete(delIds);
      } catch {
        this.recompute();
        return;
      }

      this.ctrl?.removeIssuesFor(delIds);
      if (!this.bulkFixing) this.onAfterFix?.();

      setTimeout(() => this.recompute(), 0);
    } finally {
      if (operation === this.operation) {
        this.singleFixing = false;
        this.ctrl?.setBusy(this.pending);
        this.onStateChange?.();
      }
    }
  }

  private discoverClusterInWorker(operation: number, seedIds: string[]) {
    return new Promise<ClusterReply>((resolve, reject) => {
      this.rejectRepair = reject;
      const worker = new Worker(new URL('./draw-validation.worker', import.meta.url), { type: 'module' });
      this.repairWorker = worker;
      worker.onmessage = ({ data }) => {
        if (this.repairWorker !== worker || operation !== this.operation) return;
        if (data.type === 'progress') this.showProgress(data.payload);
        else if (data.type === 'clusterRepaired') resolve(data);
        else if (data.type === 'clusterSkipped') resolve(data);
        else reject(new Error(data.message ?? 'Map overlap lookup failed'));
      };
      worker.onerror = () => reject(new Error('Map overlap worker failed'));
      worker.postMessage({ type: 'repairCluster', payload: { polys: this.getAllPolygonsWithIds(), seedIds } });
    }).finally(() => {
      if (operation === this.operation) this.releaseRepairWorker();
    });
  }

  private releaseRepairWorker() {
    this.repairWorker?.terminate();
    this.repairWorker = null;
    this.rejectRepair = undefined;
  }

  private ensureKinkLayer() {
    if (!this.map) return;
    try {
      if (!this.map.getSource(SRC_SELECTED)) this.map.addSource(SRC_SELECTED, { type: 'geojson', data: emptyFC() });
      if (!this.map.getLayer(LYR_SELECTED_FILL))
        this.map.addLayer({
          id: LYR_SELECTED_FILL,
          type: 'fill',
          source: SRC_SELECTED,
          paint: { 'fill-color': '#ffb300', 'fill-opacity': 0.3 },
        });
      if (!this.map.getLayer(LYR_SELECTED_LINE))
        this.map.addLayer({
          id: LYR_SELECTED_LINE,
          type: 'line',
          source: SRC_SELECTED,
          paint: { 'line-color': '#ffb300', 'line-width': 5 },
        });
      if (!this.map.getSource(SRC_KINKS)) {
        this.map.addSource(SRC_KINKS, { type: 'geojson', data: emptyFC() } as any);
      }
      if (!this.map.getLayer(LYR_KINKS)) {
        this.map.addLayer({
          id: LYR_KINKS,
          type: 'circle',
          source: SRC_KINKS,
          paint: {
            'circle-radius': 6,
            'circle-color': '#ff1744',
            'circle-stroke-width': 1.5,
            'circle-stroke-color': '#fff',
          },
        } as any);
      }
    } catch {}
  }

  private getAllPolygonsWithIds(): Array<{ id: string; feat: Feature<GJPolygon> }> {
    const out: Array<{ id: string; feat: Feature<GJPolygon> }> = [];
    if (!this.draw) return out;
    const fc = (this.draw.getAll() as FeatureCollection<Geometry, GeoJsonProperties>) ?? emptyFC();
    for (const f of fc.features || []) {
      if (!f?.geometry || f.geometry.type !== 'Polygon') continue;
      const id = String((f as any).id ?? '');
      if (!id) continue;
      out.push({ id, feat: orientedFeatureKeepId(f as Feature<GJPolygon>) });
    }
    return out;
  }
}
